import React from 'react';
import { Stack } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';

import { Paper, List, ListHeader, NoDataView, PageContent, Typography } from '@ocx/ui';
import { SizingMultiplier } from '@ocx-app/theme/constants';
import type { PointExpirationScheduleFragment } from '@ocx/graphql';

import { pointExpirationScheduleMessages } from './point-expiration-schedule.messages';
import { PointExpirationListItem } from './components';
import { groupExpirationsByDate } from './utils/group-expirations-by-date';

export type PointExpirationSchedulePageProps = {
  currentPoints: number;
  notificationDays: number | null | undefined;
  expirationSchedule: PointExpirationScheduleFragment[];
};

export const PointExpirationSchedulePage = (props: PointExpirationSchedulePageProps) => {
  const { expirationSchedule, notificationDays } = props;
  const intl = useIntl();

  const { expiringSoon, expiringNext } = groupExpirationsByDate(expirationSchedule, notificationDays);

  return (
    <PageContent>
      <Stack p={SizingMultiplier.sm} spacing={SizingMultiplier.sm}>
        {expirationSchedule.length === 0 && (
          <Paper sx={{ py: 16 }}>
            <NoDataView
              title={intl.formatMessage(
                pointExpirationScheduleMessages['point-expiration-schedule:no-expiring-points'],
              )}
              description={intl.formatMessage(
                pointExpirationScheduleMessages['point-expiration-schedule:no-expiring-points-description'],
              )}
            />
          </Paper>
        )}

        {expiringSoon.length > 0 && (
          <Paper>
            <List disabledPaddings>
              <ListHeader>
                <FormattedMessage id="point-expiration-schedule:expiring-soon-header" defaultMessage="Expiring soon" />
                <Typography variant="body2" color="text.disabled" fontWeight="600" mt={SizingMultiplier.xs}>
                  Updated Nightly
                </Typography>
              </ListHeader>
              {expiringSoon.map((item) => (
                <PointExpirationListItem key={item.date} item={item} isExpiringSoon />
              ))}
            </List>
          </Paper>
        )}

        {expiringNext.length > 0 && (
          <Paper>
            <List disabledPaddings>
              <ListHeader>
                <FormattedMessage id="point-expiration-schedule:expiring-next-header" defaultMessage="Expiring Next" />
              </ListHeader>
              {expiringNext.map((item) => (
                <PointExpirationListItem key={item.date} item={item} isExpiringSoon={false} />
              ))}
            </List>
          </Paper>
        )}
      </Stack>
    </PageContent>
  );
};
